/**
 * Code folding for the template editor (SET-040), as plain text functions.
 *
 * The editor is a real <textarea>, which cannot hide lines. So a folded
 * region is taken OUT of the text the textarea shows and kept aside, and a
 * marker — one private-use character, unique to the fold — is left at the end
 * of the line that opened it. Whatever is typed in the shown text is put back
 * into the whole template by swapping each marker for what it stands for.
 * Deleting a marker deletes its region, as deleting a folded block does in any
 * editor. The stored template never contains a marker.
 *
 * A region is what is indented under a line: the lines after it that sit
 * deeper than it does (blank lines among them included). That folds a PUG
 * block, an HTML element laid out one tag per line, and a {{#each}} block the
 * same way, without parsing either language.
 */

export interface Fold {
  /** The private-use character standing in for the region. */
  readonly mark: string
  /** The region's lines, joined, which may hold other folds' marks. */
  readonly hidden: string
}

const FIRST_MARK = 0xe000
const LAST_MARK = 0xf8ff
export const MARKS = /[\uE000-\uF8FF]/g
const HAS_MARK = /[\uE000-\uF8FF]/

const indentOf = (line: string): number => /^[ \t]*/.exec(line)![0].replace(/\t/g, '  ').length
const blank = (line: string): boolean => line.replace(MARKS, '').trim() === ''

/**
 * The region a line opens: from the next line to the last one indented deeper
 * than it, or null when nothing sits under it.
 */
export const regionAt = (lines: readonly string[], at: number): { from: number; to: number } | null => {
  const head = lines[at]
  if (head === undefined || blank(head)) return null
  const base = indentOf(head)
  let to = -1
  for (let i = at + 1; i < lines.length; i += 1) {
    const line = lines[i]!
    if (blank(line)) continue
    if (indentOf(line) <= base) break
    to = i
  }
  return to === -1 ? null : { from: at + 1, to }
}

/** A mark no fold is using yet. */
const freshMark = (folds: readonly Fold[]): string => {
  const used = new Set(folds.map((f) => f.mark))
  for (let c = FIRST_MARK; c <= LAST_MARK; c += 1) {
    const mark = String.fromCharCode(c)
    if (!used.has(mark)) return mark
  }
  throw new Error('Too many folds')
}

/** Fold the region under shown line `at`. Unchanged when the line opens none. */
export const foldAt = (shown: string, folds: readonly Fold[], at: number): { shown: string; folds: Fold[] } => {
  const lines = shown.split('\n')
  const region = regionAt(lines, at)
  if (!region) return { shown, folds: [...folds] }
  const mark = freshMark(folds)
  const hidden = lines.slice(region.from, region.to + 1).join('\n')
  const next = [...lines.slice(0, at), `${lines[at]}${mark}`, ...lines.slice(region.to + 1)]
  return { shown: next.join('\n'), folds: [...folds, { mark, hidden }] }
}

/** Open one fold again, in place. */
export const unfold = (shown: string, folds: readonly Fold[], mark: string): { shown: string; folds: Fold[] } => {
  const fold = folds.find((f) => f.mark === mark)
  if (!fold) return { shown, folds: [...folds] }
  return { shown: shown.replace(mark, `\n${fold.hidden}`), folds: folds.filter((f) => f !== fold) }
}

/**
 * The whole template behind the shown text: every mark swapped for its
 * region, nested ones included. A mark with no fold behind it is dropped.
 */
export const expand = (shown: string, folds: readonly Fold[]): string => {
  const byMark = new Map(folds.map((f) => [f.mark, f.hidden]))
  let text = shown
  // Each pass opens one level of nesting; a fold cannot hold itself.
  for (let pass = 0; pass <= folds.length && HAS_MARK.test(text); pass += 1) {
    text = text.replace(MARKS, (m) => (byMark.has(m) ? `\n${byMark.get(m)!}` : ''))
  }
  return text.replace(MARKS, '')
}

/** The folds whose mark is still somewhere in the shown text or inside another live fold. */
export const liveFolds = (shown: string, folds: readonly Fold[]): Fold[] => {
  const live: Fold[] = []
  let pool = shown
  let changed = true
  while (changed) {
    changed = false
    for (const f of folds) {
      if (!live.includes(f) && pool.includes(f.mark)) {
        live.push(f)
        pool += f.hidden
        changed = true
      }
    }
  }
  return live
}

/**
 * The real line number of every shown line, 1-based: a folded line stands
 * for itself and everything folded under it.
 */
export const lineNumbers = (shown: string, folds: readonly Fold[]): number[] => {
  const byMark = new Map(folds.map((f) => [f.mark, f.hidden]))
  const linesIn = (text: string): number => {
    let n = 0
    for (const ch of text) {
      if (ch === '\n') n += 1
      else if (byMark.has(ch)) n += 1 + linesIn(byMark.get(ch)!)
    }
    return n
  }
  const out: number[] = []
  let real = 1
  for (const line of shown.split('\n')) {
    out.push(real)
    real += 1 + linesIn(line)
  }
  return out
}
